import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import { ZoneMetadataFieldsWithDefaults, ZoneMetadataSchemaTypes } from "../../types/zoneMetadata.d.js";
import { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "ZoneMetadata" as const;

/**
 * Matches Zod's `.positive()` on the planting lengths. Mongoose's `min` is inclusive, so `min: 0` would
 * store a 0 the DTO refuses. Not run on an unset value, which is how a cleared length is stored.
 */
const GREATER_THAN_ZERO = {
    validator: (value: number): boolean => value > 0,
    message: "{PATH} must be greater than 0",
};

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ZoneMetadata>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ZoneMetadata extends SchemaBase<ZoneMetadata, ZoneMetadataFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public varietal?: string;

    /** Broad crop identity; varietal remains grower-entered free text. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public cropType?: string;

    /** Planting geometry in metres. These are independent of irrigation emitter spacing. */
    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public rowSpacing?: number;

    /** Preferred display unit for row spacing; the measured value remains in metres. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public rowSpacingDisplayUnit?: "meter" | "foot" | "inch";

    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public plantSpacing?: number;

    /** Preferred display unit for plant spacing; independent of the row spacing unit. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public plantSpacingDisplayUnit?: "meter" | "foot" | "inch";

    /** Typical row length in metres, for counting plants by row rather than by area. */
    @validate({ zodValidator: z.number().positive().optional() })
    @prop({ type: () => Number, required: false, validate: GREATER_THAN_ZERO })
    public rowLength?: number;

    /** Preferred display unit for row length; the measured value remains in metres. */
    @validate({ zodValidator: z.enum(["meter", "foot", "inch"]).optional() })
    @prop({ type: () => String, required: false })
    public rowLengthDisplayUnit?: "meter" | "foot" | "inch";

    /** Number of planted rows in the zone. */
    @validate({ zodValidator: z.number().int().positive().optional() })
    @prop({ type: () => Number, required: false, min: 1 })
    public rowCount?: number;

    /** Rootstock name, independent of the legacy rootClone field. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public rootstock?: string;

    /** Clonal selection within a cultivar: clone for grapes, strain or sport for tree fruit. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public clonalSelection?: string;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public datePlanted?: Date;

    /**
     * in meters
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterSpacing?: number;

    /**
     * in gallons per hour
     * @type {number}
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public emitterFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public rootClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public vineClone?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public notes?: string;

    /** The pressure the zone needs, as metres of water head. Nothing reads it yet. */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public zonePressureHead?: number;

    /**
     * The whole zone's flow while irrigating, in litres per hour. The irrigation report multiplies
     * irrigated hours by it for its theoretical volumes.
     */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public totalZoneFlowRate?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public connectedPump?: string;

    static dtoValidators: DtoValidatorTypes<ZoneMetadataSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ZoneMetadata, ZoneMetadataFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ZoneMetadata.dtoValidators = getZodSchemaFromClass(
    ZoneMetadata,
) as unknown as DtoValidatorTypes<ZoneMetadataSchemaTypes>;
